
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ImageUpload } from "@/components/ui/image-upload"
import { courseService } from "@/services/course.service"
import { useToast } from "@/hooks/use-toast"
import { Loader2 } from "lucide-react"
import type { Author } from "@/types/api"

const formSchema = z.object({
    full_name: z.string().min(2, "Имя должно содержать не менее 2 символов"),
    slug: z.string().optional().refine(
        (val) => !val || (val.length >= 2 && val.length <= 50 && /^[a-z0-9-]+$/.test(val)),
        "Слаг может содержать только английские буквы (a-z), цифры и дефисы (макс. 50 символов)"
    ),
    bio: z.string().min(10, "Биография должна содержать не менее 10 символов"),
    image: z.any().optional(),
})

interface AuthorDialogProps {
    author: Author | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

export function AuthorDialog({
    author,
    open,
    onOpenChange,
    onSuccess,
}: AuthorDialogProps) {
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            full_name: "",
            slug: "",
            bio: "",
            image: null,
        },
    })

    useEffect(() => {
        if (author) {
            form.reset({
                full_name: author.full_name,
                slug: undefined,
                bio: author.bio,
                image: author.image,
            })
        } else {
            form.reset({
                full_name: "",
                slug: "",
                bio: "",
                image: null,
            })
        }
    }, [author, form, open])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsLoading(true)
        try {
            const formData = new FormData()
            formData.append("full_name", values.full_name)
            formData.append("bio", values.bio)
            if (!author && values.slug) {
                formData.append("slug", values.slug)
            }
            if (values.image instanceof File) {
                formData.append("image", values.image)
            }

            if (author) {
                await courseService.updateAuthor(author.slug, formData)
                toast({
                    title: "Успешно",
                    description: "Автор успешно обновлен",
                })
            } else {
                await courseService.createAuthor(formData)
                toast({
                    title: "Успешно",
                    description: "Автор успешно создан",
                })
            }
            onSuccess()
            onOpenChange(false)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: `Не удалось ${author ? "обновить" : "создать"} автора`,
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{author ? "Редактировать автора" : "Создать автора"}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="image"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Изображение</FormLabel>
                                    <FormControl>
                                        <ImageUpload
                                            value={field.value}
                                            onChange={field.onChange}
                                            label="Загрузить изображение"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="full_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Полное имя</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {!author && (
                            <FormField
                                control={form.control}
                                name="slug"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Слаг</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="john-doe" maxLength={50} />
                                        </FormControl>
                                        <FormDescription>
                                            Используйте только английские буквы, цифры и дефисы (макс. 50 символов)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <FormField
                            control={form.control}
                            name="bio"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Биография</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {author ? "Сохранение..." : "Создание..."}
                                    </>
                                ) : (
                                    author ? "Сохранить изменения" : "Создать"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
